import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import WaitingIncomingCallAuthorizer from './WaitingIncomingCallAuthorizer';
import WaitingCallsBadge from './WaitingCallsBadge';
import WaitingCallsDropdown, { WAITING_CALLS_DROPDOWN_OFFSET_PX } from './WaitingCallsDropdown';
import { HEADER_WHATSAPP_CALLS_BUTTON_ROOT_ID } from './headerWhatsappCallsIds';
import { WS_CALL_CLIENT_LIST_SIDEBAR } from '../../hooks/calls/callUiConstants';
import useWaitingCallsQueue from '../../hooks/calls/useWaitingCallsQueue';
import useHeaderWhatsappCallsAnchor from '../../hooks/calls/useHeaderWhatsappCallsAnchor';
import useWaitingCallsPulse from '../../hooks/calls/useWaitingCallsPulse';
import { canUserReceiveCalls } from '../../utils/callsPermissionsUtils';

function computeMenuPosition(anchor) {
  if (!anchor) return null;
  const rect = anchor.getBoundingClientRect();
  return {
    top: rect.bottom + WAITING_CALLS_DROPDOWN_OFFSET_PX,
    right: Math.max(8, window.innerWidth - rect.right)
  };
}

const HeaderWhatsappCallsButtonMC = memo(({
  incomingPhones,
  activeCall,
  updateIncomingPhone,
  onDismiss
}) => {
  const { t } = useTranslation('Fonema');
  const currentRetailerUser = useSelector((state) => state.mainReducer?.currentRetailerUser);
  const canReceiveCalls = canUserReceiveCalls(currentRetailerUser);

  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const panelRef = useRef(null);

  const { anchorEl, anchorRef, bindClick } = useHeaderWhatsappCallsAnchor(
    HEADER_WHATSAPP_CALLS_BUTTON_ROOT_ID
  );

  const {
    waitingCalls,
    waitingCallIdsKey,
    visibleCount,
    authorizedCalls,
    handleAuthorizationResolved
  } = useWaitingCallsQueue({
    incomingPhones,
    activeCall,
    canReceiveCalls,
    onDismiss
  });

  useWaitingCallsPulse(anchorEl, visibleCount);

  useEffect(() => {
    setIsOpen(false);
    setMenuPosition(null);
  }, [waitingCallIdsKey]);

  useEffect(() => {
    if (visibleCount > 0) return undefined;
    setIsOpen(false);
    setMenuPosition(null);
    return undefined;
  }, [visibleCount]);

  const refreshMenuPosition = useCallback(() => {
    setMenuPosition(computeMenuPosition(anchorRef.current));
  }, [anchorRef]);

  useLayoutEffect(() => {
    if (!isOpen || visibleCount === 0) {
      setMenuPosition(null);
      return;
    }
    refreshMenuPosition();
  }, [isOpen, visibleCount, refreshMenuPosition]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleReposition = () => refreshMenuPosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    const handlePointerDown = (event) => {
      const { target } = event;
      if (panelRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      setIsOpen(false);
    };

    const listenerTimer = window.setTimeout(() => {
      document.addEventListener('mousedown', handlePointerDown);
    }, 0);

    return () => {
      window.clearTimeout(listenerTimer);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [isOpen, refreshMenuPosition, anchorRef]);

  const openClientListSidebar = useCallback(() => {
    if (typeof window.openSidebarMC === 'function') {
      window.openSidebarMC(WS_CALL_CLIENT_LIST_SIDEBAR);
    }
  }, []);

  const handleAnchorClick = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();

    if (visibleCount > 0) {
      setIsOpen((open) => {
        const next = !open;
        if (next) {
          setMenuPosition(computeMenuPosition(anchorRef.current));
        }
        return next;
      });
      return;
    }

    setIsOpen(false);
    setMenuPosition(null);
    openClientListSidebar();
  }, [visibleCount, openClientListSidebar, anchorRef]);

  useEffect(() => bindClick(handleAnchorClick), [bindClick, handleAnchorClick]);

  useEffect(() => {
    if (!anchorEl) return undefined;

    if (visibleCount > 0) {
      anchorEl.setAttribute(
        'title',
        t('waiting_incoming_calls_title', { count: visibleCount })
      );
      anchorEl.setAttribute(
        'aria-label',
        t('waiting_incoming_calls_aria', { count: visibleCount })
      );
    } else {
      anchorEl.removeAttribute('title');
      anchorEl.setAttribute('aria-label', t('open_call_client_list_aria'));
    }

    return undefined;
  }, [anchorEl, visibleCount, t]);

  if (!anchorEl) {
    return null;
  }

  const shouldAuthorizeWaitingCalls = canReceiveCalls && waitingCalls.length > 0;

  const authorizers = shouldAuthorizeWaitingCalls && createPortal(
    <div className="tw-wrapper tw-hidden" aria-hidden>
      {waitingCalls.map((call) => (
        <WaitingIncomingCallAuthorizer
          key={`auth-${call.callId}`}
          callInfoData={call}
          updateIncomingPhone={updateIncomingPhone}
          onDismiss={onDismiss}
          onAuthorizationResolved={handleAuthorizationResolved}
        />
      ))}
    </div>,
    document.body
  );

  return (
    <>
      {authorizers}
      <WaitingCallsBadge anchorEl={anchorEl} visibleCount={visibleCount} />
      <WaitingCallsDropdown
        isOpen={isOpen}
        menuPosition={menuPosition}
        visibleCount={visibleCount}
        authorizedCalls={authorizedCalls}
        panelRef={panelRef}
      />
    </>
  );
});

HeaderWhatsappCallsButtonMC.displayName = 'HeaderWhatsappCallsButtonMC';

export default HeaderWhatsappCallsButtonMC;
